import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ACTION_BEAT_ATTR } from '../../ui/actionBeatMotion';
import { KB_LIFT_MS } from '../../components/common/keyboardOverlayStyles';
import { CLOSE_START_MS } from './modalCloseChoreography';
import { kbLog } from '../../utils/ui/keyboardReframeDebug';

/**
 * REST RELEASE — the action beat plays CENTRED (founder, 2026-10-22, after
 * KAN-930: "once we finish something, the success … animation needs to show
 * centered"). Since KAN-1181 this hook only decides WHEN; the move itself is
 * the anticipation hook's close choreography.
 *
 * A touch pop-up is a rest card (`useModalTypingRest`, from a tap on a typing
 * field) only while it is in its TYPING phase. The moment its confirm starts
 * committing — any beat visual inside the panel carries `data-cvx-action-beat`
 * (ModalActionBeat, ActionBeatFill, ActionBeatStage / ActionBeatRing; every
 * `useModalActionBeat` modal renders one of them) — and the keyboard is down,
 * `released` flips true (once CLOSE_START_MS after the keyboard closed): the
 * shell drops `data-cvx-kb-rest` and the anticipation hook glides the card to
 * the centre on its padding-top (`restReleased`).
 *
 * `resetKey` changes on every NEW tap-rest engage: the latch resets, so a tap
 * on a typing field after the beat ended in an error (wrong code, failed send)
 * glides the card up again exactly like the first tap.
 *
 * Scope: ONLY the action beat. A step change that removes the typing field
 * does NOT release.
 */

/** Dismiss-suppression past the glide's end (a release landing just after it). */
export const GLIDE_SUPPRESS_PAD_MS = 120;
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

function hasBeat(el) {
  return !!(el && typeof el.querySelector === 'function' && el.querySelector(`[${ACTION_BEAT_ATTR}]`));
}

/**
 * @param {{ dialogEl: HTMLElement|null, mounted: boolean, rest: boolean,
 *   keyboardOpen: boolean, visible: boolean, resetKey?: number,
 *   suppressDismiss?: (ms: number) => void }} args
 * @returns {boolean} released — true once the beat sent the card to the centre (until the next tap-rest).
 */
export default function useModalRestRelease({
  dialogEl, mounted, rest, keyboardOpen, visible, resetKey = 0, suppressDismiss = null,
}) {
  const [released, setReleased] = useState(false);
  const suppressRef = useRef(suppressDismiss);
  suppressRef.current = suppressDismiss;
  const [beatActive, setBeatActive] = useState(false);
  const kbClosedAtRef = useRef(-Infinity);
  const prevKbOpenRef = useRef(keyboardOpen);
  const prevKeyRef = useRef(resetKey);

  // Fresh per open.
  useLayoutEffect(() => {
    if (mounted) return;
    if (released) setReleased(false);
    if (beatActive) setBeatActive(false);
    kbClosedAtRef.current = -Infinity;
  }, [mounted, released, beatActive]);

  // A new tap-rest session starts clean (a stale beat flag must not release it at once).
  useLayoutEffect(() => {
    if (prevKeyRef.current === resetKey) return;
    prevKeyRef.current = resetKey;
    setReleased(false);
    setBeatActive(false);
  }, [resetKey]);

  // When did the keyboard last close? (the close choreography's clock).
  useEffect(() => {
    if (prevKbOpenRef.current && !keyboardOpen) kbClosedAtRef.current = now();
    prevKbOpenRef.current = keyboardOpen;
  }, [keyboardOpen]);

  // Watch the panel for a beat visual — no per-modal wiring.
  const watching = rest && !released && !!dialogEl;
  useEffect(() => {
    if (!watching) return undefined;
    const check = () => setBeatActive(hasBeat(dialogEl));
    check();
    if (typeof MutationObserver === 'undefined') return undefined;
    const mo = new MutationObserver(check);
    mo.observe(dialogEl, {
      subtree: true, childList: true, attributes: true, attributeFilter: [ACTION_BEAT_ATTR],
    });
    return () => mo.disconnect();
  }, [watching, dialogEl]);

  // Release once: beat on, keyboard down, close choreography already started.
  useEffect(() => {
    if (!watching || !beatActive || keyboardOpen || !visible) return undefined;
    const wait = Math.max(0, kbClosedAtRef.current + CLOSE_START_MS - now());
    const t = setTimeout(() => {
      // The card moves under the finger: a tap that began on it must never
      // resolve against the scrim and dismiss.
      if (suppressRef.current) suppressRef.current(KB_LIFT_MS + GLIDE_SUPPRESS_PAD_MS);
      kbLog('restRelease', { wait: Math.round(wait) });
      setReleased(true);
    }, wait);
    return () => clearTimeout(t);
  }, [watching, beatActive, keyboardOpen, visible, dialogEl]);

  return released;
}
